import { ChatBot, NextPageHead, PageView, VWOExperimentPush } from '@aspendental/shared-components-web';
import { convertNameToUrlFormat } from '@aspendental/shared-components-web/lib/utils/convertNameToUrlFormat/convertNameToUrlFormat';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { getBeffeGraphQlUrl, getRootLevelConfig, getTagTheme } from '../utils';
import DetermineIndexing from '../utils/determine-indexing';
import getHostFromHeaders from '../utils/get-host-from-headers';

import ProviderDetailsPage from '@src/components/ProviderDetailsPage/ProviderDetailsPage';
import { NOT_FOUND } from '@src/constants';
import {
	AppMetadataByAppNameDocument,
	AppPagesDataByAppNameDocument,
	ProviderDetailsByIdDocument,
	ProviderDetailsPageByIdDocument,
	ProviderPicByNpiDocument,
	type Page,
} from '@src/lib/__generated/sdk';
import { getBeffeGraphQlClient, getEnterpriseAPIGraphQlClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import choosePreview from '@src/pages/utils/choose-preview';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import { getUserCoordinates } from '@src/pages/utils/get-user-coordinates';
import { getBffProviderReviews, getBffProviderReviewSummary } from '@src/services/bffFacilityCalls';
import { cleanProviderPicByNpi } from '@src/util/CleanProviderPicByNpi';
import { interpolateContent } from '@src/util/contentVariableInsertion';
import { extractFaqItemsFromContents, generateProviderSchemaObject } from '@src/util/providerJsonSchema';
import { buildProviderReviews, buildSelectedReviews } from '@src/util/providerReviews';

const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
	const { config, showProviderPageInsuranceInfo, googleMapsData, reviews, selectedReviews } = props;
	const { siteURL, features, featureFlags } = config as BrandThemeConfig;
	const { url: logoUrl } = props.appData?.logo ?? {};
	const { doctorDetails, path, breadcrumbsData, providerPageContent, pageReferrer, preview, domain } = props;
	const { firstName, lastName, mainWorkerFunction, providerLocations } = doctorDetails.provider;

	// page title and desc
	// can be updated based on seo recs later
	let pageTitle = `${firstName} ${lastName} | ${mainWorkerFunction} | ${config.name}`;
	let pageDesc = `Come meet ${firstName} ${lastName}, an expert ${mainWorkerFunction}. At ${config.name}, we offer quality & affordable dental care. Schedule your free consultation now.`;

	if (config?.name === 'Aspen Dental') {
		const mainLocation = providerLocations?.find(({ isMain }) => isMain);
		const mainCity = mainLocation?.facility?.location?.city || '';
		pageTitle = `${firstName} ${lastName}, ${mainWorkerFunction} in ${mainCity}`;
		pageDesc = `Meet ${firstName} ${lastName}, a trusted ${mainWorkerFunction} at Aspen Dental. Learn about their experience and commitment to personalized, affordable care. Book your appointment today.`;
	}

	const fullProviderPath = `${features.providers.root}${convertNameToUrlFormat(`${firstName} ${lastName}`)}${path}`;

	const allProviderPageContents = [
		...(providerPageContent?.contentsCollection?.items ?? []),
		...(providerPageContent?.secondaryContentsCollection?.items ?? []),
	];
	const faqItems = extractFaqItemsFromContents(allProviderPageContents);

	return (
		<>
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} />
			<PageView siteSection="Office Details" pageReferrer={pageReferrer} />
			<NextPageHead
				path={fullProviderPath}
				title={pageTitle}
				indexed={DetermineIndexing(true, props.env, preview, featureFlags.doNotIndexWholeSite, domain)}
				description={pageDesc}
				canonical={`${siteURL}${fullProviderPath}`}
				originUrl={siteURL}
				breadcrumbTitle={breadcrumbsData.breadcrumbTitle}
				breadcrumbs={breadcrumbsData.breadcrumbsCollection.items}
				ogImage={
					providerPageContent?.ogImage?.desktopImage?.image?.cdn_url ||
					doctorDetails.providerPicByNpi?.[0]?.url ||
					''
				}
				contentsCollection={
					features?.providerSchema
						? ({
								items: generateProviderSchemaObject(
									config,
									doctorDetails,
									path,
									pageTitle,
									pageDesc,
									faqItems
								)['@graph'].map((node: object) => ({
									__typename: 'Schema' as const,
									combinedSchema: true,
									schema: node,
								})),
								limit: 0,
								skip: 0,
								total: 0,
						  } as any)
						: undefined
				}
			/>
			<ProviderDetailsPage
				provider={{ ...doctorDetails.provider, providerImages: doctorDetails.providerPicByNpi }}
				reviews={reviews}
				selectedReviews={selectedReviews}
				config={config}
				contentsCollection={providerPageContent?.contentsCollection}
				secondaryContentsCollection={providerPageContent?.secondaryContentsCollection}
				showProviderPageInsuranceInfo={showProviderPageInsuranceInfo}
				googleMapsData={googleMapsData}
			/>
			{features.chat.enabled && (
				<ChatBot
					url={features.chat.chatBotURL}
					isIconButton={features.chat.isIconButton}
					logoUrl={logoUrl}
					featureList={featureFlags.featureList}
				/>
			)}
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ params, locale, req, res, query, preview = false }) => {
	if (!params?.slug) {
		return NOT_FOUND;
	}
	const deviceType = getDeviceTypeFromHeaders(req.headers);
	const loggerName = 'provider-detail-page';
	const tagTheme = getTagTheme(req);
	const logger = getLogger('provider-detail-page', tagTheme);
	const slugs = params.slug as string[];
	// this path value needed for page indexing
	const path = '/' + slugs.join('/');
	const pageReferrer = req.headers.referer || '';
	const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';

	try {
		const domain = getHostFromHeaders(req.headers);
		preview = choosePreview(domain, preview);

		const { brandConfig, gqlClient, appName } = getRootLevelConfig({
			req,
			preview,
			loggerName,
		});
		logger.info(path);

		const [appPagesData, appMetadata, translations] = await Promise.all([
			gqlClient.query(AppPagesDataByAppNameDocument, { appName, preview }).toPromise(),
			gqlClient.query(AppMetadataByAppNameDocument, { appName, preview }).toPromise(),
			getServerSideTranslations(locale),
		]);
		const appData = {
			...appPagesData.data?.appCollection?.items[0],
			...appMetadata.data?.appCollection?.items[0],
		};

		// replace [env] with environment name for locationBarFindOfficeUrl
		// TODO: create a brand variable context to replace those variables from CMS in future
		if (appData && appData.header?.locationBarFindOfficeUrl) {
			const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
			appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace(
				'[env]',
				brandSubdomain
			);
		}

		if (!appData) {
			logger.error('PP_APP_ERROR');
			return NOT_FOUND;
		}

		//only the last section in the url path is the slug to query
		const pathForQuery = slugs[slugs.length - 1];
		const enterpriseAPIGraphQlClient = getEnterpriseAPIGraphQlClient();
		const updatedServices = getConfigServices(brandConfig);
		const beffeGraphQlUrl = getBeffeGraphQlUrl(updatedServices);
		const providersExclusionListArr = appData?.featureFlags?.providersExclusionList ?? [];
		const providersExclusionList = new Set(providersExclusionListArr);

		if (providersExclusionList.has(pathForQuery)) {
			logger.warn(`PP_EXCLUDED_PROVIDER: ${pathForQuery}`);
			return NOT_FOUND;
		}

		logger.info(`PP_BEFFE_URL: ${beffeGraphQlUrl}`);

		const beffeGraphQlClient = getBeffeGraphQlClient(beffeGraphQlUrl);
		const [providerData, providerPicturesData] = await Promise.all([
			enterpriseAPIGraphQlClient.query(ProviderDetailsByIdDocument, { npi: pathForQuery }).toPromise(),
			beffeGraphQlClient
				.query(ProviderPicByNpiDocument, { npi: pathForQuery, brand: brandConfig.bffBrandType })
				.toPromise(),
		]);

		if (providerData.error) {
			logger.error(`COULD_NOT_FIND_THE_PROVIDER_SLUG: ${pathForQuery} (${providerData.error})`);
			return NOT_FOUND;
		}

		if (!providerData.data.provider) {
			logger.error(`INVALID_PROVIDER_NUMBER: ${pathForQuery}`);
			return NOT_FOUND;
		}

		if (providerPicturesData.error) {
			logger.error(`COULD_NOT_FIND_THE_PROVIDER_PICTURES_SLUG: ${pathForQuery} (${providerPicturesData.error})`);
			providerPicturesData.data = {
				providerPicByNpi: [],
			};
		}

		const showProviderReviews = appData.featureFlags?.featureList?.includes('showProviderReviews') ?? false;

		logger.info(
			`PP_REVIEWS_FLAG: loadFacilityReviewsFromBeffe=${appData.featureFlags.loadFacilityReviewsFromBeffe}, showProviderReviews=${showProviderReviews}, npi=${pathForQuery}`
		);

		const [_providerReviews, _providerReviewSummary] =
			showProviderReviews && appData.featureFlags.loadFacilityReviewsFromBeffe
				? await Promise.all([
						getBffProviderReviews(brandConfig.bffBrandType, pathForQuery, beffeGraphQlUrl),
						getBffProviderReviewSummary(brandConfig.bffBrandType, pathForQuery, beffeGraphQlUrl),
				  ])
				: [undefined, undefined];

		logger.info(
			`PP_REVIEWS_RESULT: summary=${JSON.stringify(
				_providerReviewSummary?.data?.reviewSummaryByProvider
			)}, reviewCount=${_providerReviews?.data?.reviewsByProvider?.length}`
		);

		const doctorDetails = {
			provider: {
				...providerData.data.provider,
				brand: brandConfig.bffBrandType,
			},
			...providerPicturesData.data,
		};

		doctorDetails.providerPicByNpi = cleanProviderPicByNpi(doctorDetails?.providerPicByNpi);

		// breadcrumb data generation for provider page
		const breadcrumbsData = {
			breadcrumbTitle: `${doctorDetails.provider.firstName} ${doctorDetails.provider.lastName}`,
			path: path,
			breadcrumbsCollection: {
				items: [
					{
						breadcrumbTitle: 'Home',
						path: '/',
					} as Page,
					{
						breadcrumbTitle: 'Providers',
						path: brandConfig.features.providers.root,
					},
				],
			},
		};

		// contentful contents for provider page
		let pageContentData = await gqlClient
			.query(ProviderDetailsPageByIdDocument, { brandName: brandConfig.name, npiId: pathForQuery, preview })
			.toPromise();
		const providerDetailsPageCollectionItems = pageContentData?.data?.providerDetailsPageCollection?.items || [];
		if (providerDetailsPageCollectionItems.length !== 1) {
			pageContentData = await gqlClient
				.query(ProviderDetailsPageByIdDocument, { brandName: brandConfig.name, npiId: 'Default', preview })
				.toPromise();
		}
		const providerPageContent = pageContentData?.data?.providerDetailsPageCollection?.items[0];

		const pageStructure = {
			title: '',
			description: '',
			canonical: '',
			...providerPageContent,
		};

		const mainFacilityLocation = doctorDetails.provider.providerLocations?.find(({ isMain }) => isMain);
		const mainFacilityName = mainFacilityLocation?.facility?.name || '';
		const mainFacilityCity = mainFacilityLocation?.facility?.location?.city || '';
		const mainFacilityState = mainFacilityLocation?.facility?.location?.stateCode || '';

		const interpolatedPageData = interpolateContent(pageStructure as any, [
			{ value: mainFacilityName, regex: /\[name\]/g },
			{ value: mainFacilityCity, regex: /\[city\]/g },
			{ value: mainFacilityState, regex: /\[state\]/g },
		]);

		// Resolve VWO FME feature flags for layout-level header swap
		const { flags: serverFeatureFlags, decisions: flagDecisions } = await resolveFeatureFlags(
			LAYOUT_FEATURE_FLAGS,
			brandConfig,
			req,
			res,
			query,
			logger,
			mainFacilityLocation?.facility?.code ?? undefined
		);

		return {
			props: {
				...translations,
				env: {
					ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
				},
				config: {
					...brandConfig,
					...(appData.featureFlags ? { featureFlags: appData.featureFlags } : {}),
					services: updatedServices,
					schemaUrl: `${brandConfig.siteURL}${req.url}`,
				},
				doctorDetails: doctorDetails,
				...(interpolatedPageData ? { providerPageContent: interpolatedPageData } : {}),
				appData,
				user: getUserCoordinates(req, logger),
				footerData: appData?.footer,
				path,
				breadcrumbsData,
				preview,
				deviceType,
				showProviderPageInsuranceInfo: appData?.featureFlags?.showProviderPageInsuranceInfo,
				googleMapsData: appData?.header?.googleMapsForLocationCards,
				pageReferrer,
				stateCodeFromHeaders,
				domain: domain ?? '',
				reviews: showProviderReviews
					? buildProviderReviews(_providerReviewSummary?.data, _providerReviews?.data)
					: null,
				selectedReviews: showProviderReviews ? buildSelectedReviews(_providerReviews?.data) : null,
				vwoDecisions: flagDecisions,
				serverFeatureFlags,
			},
		};
	} catch (e) {
		logger.error(`PROVIDER_DETAIL_PAGE_LOAD_FAILURE: PATH - ${path} ERROR - ${e}`);
		return NOT_FOUND;
	}
};

export default Page;
